import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { openWebCenterDecisionCall } from '@/lib/analytics';
import {
  ArrowRight,
  Calendar,
  HelpCircle,
  CheckCircle2,
  XCircle,
  Compass,
  Lightbulb,
  ShieldCheck,
  Newspaper,
  Map,
  MessageCircleQuestion,
} from 'lucide-react';
import Navigation from '@/components/Navigation';
import SEO from '@/components/SEO';
import Breadcrumbs from '@/components/Breadcrumbs';
import WebCenterEngagementHistory from '@/components/WebCenterEngagementHistory';
import LastUpdated from '@/components/LastUpdated';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { FOUNDER, founderPersonLd } from '@/lib/founder';

const KEY_FACTS = [
  {
    icon: CheckCircle2,
    title: '14c (14.1.2) shipped in 2024',
    detail:
      'Oracle Fusion Middleware 14c — release 14.1.2 — was published in 2024 and is the latest major version of the WebCenter product family, including WebCenter Content (UCM), Imaging, Enterprise Capture, Forms Recognition, Portal, and Sites.',
  },
  {
    icon: ShieldCheck,
    title: 'Premier Support through December 2030',
    detail:
      "Per Oracle's Lifetime Support Policy, WebCenter 14c carries Premier Support through December 2030 — bug fixes, security updates, OS certifications, and new feature delivery.",
  },
  {
    icon: ShieldCheck,
    title: 'Extended Support through December 2033',
    detail:
      'Extended Support runs through December 2033, with the typical security-and-critical-patch scope. That puts approximately 8-9 years of supported runway on 14c from a 2026 decision point.',
  },
  {
    icon: Lightbulb,
    title: 'Active investment continues',
    detail:
      'Recent 14c work has focused on OCI deployment patterns, REST API surfaces across WCC and Capture, and integration points for Oracle AI services (Document Understanding, Generative AI). This is not a frozen product.',
  },
];

const RUMOR_SOURCES = [
  {
    icon: Newspaper,
    title: 'The December 2026 12c cliff is real',
    detail:
      "12c Premier Support ends December 2026 and Extended Support is unusually short — only 12 months, through December 2027. That is a real deadline for customers on 12c, and the urgency it creates can sound like 'WebCenter is being killed' when it is actually 'a version is being retired on schedule.'",
  },
  {
    icon: Compass,
    title: 'Oracle is investing heavily in OCI-native alternatives',
    detail:
      'OCI Document Understanding, Oracle Process Automation, and Content Management Cloud are all real, growing products. From the outside they look like substitutes for WebCenter Capture, BPM, and Content. They are not direct replacements — but the investment signal is loud and easy to misread.',
  },
  {
    icon: MessageCircleQuestion,
    title: 'The WebCenter partner ecosystem has thinned',
    detail:
      "Fewer consulting firms market WebCenter expertise visibly. Marketing presence at Oracle events skews toward Fusion Apps and OCI. The 'where did everyone go?' perception is real even though the product itself remains supported and developed.",
  },
];

const WHATS_TRUE = [
  {
    title: '14c is the supported forward path for customers staying on the stack',
    detail:
      'If the WebCenter architecture fits the business — Forms Recognition rules library, FIPSA accelerator, SOA composites, Managed Attachments for EBS, ADF non-PO coding forms — then the 12c → 14c upgrade is a legitimate, Oracle-recommended path that buys approximately 8 years of additional supported runway.',
  },
  {
    title: 'Modernizing off the stack is also a legitimate forward path',
    detail:
      'For customers who would rather not face the same lifecycle decision again in 2030-2032, modernizing the AP automation layer to an AI-driven platform — while keeping Oracle EBS or Fusion as the system of record — is a real option. Path A (upgrade) and Path B (modernize) are both rational; neither is forced.',
  },
  {
    title: 'Sustaining Support exists for customers who need more time',
    detail:
      'Sustaining Support has no end date and provides access to existing fixes, patches, and updates — though no new patches or tax/legal/regulatory updates. It is a holding pattern, not a destination, but it is a real option for customers whose decision window extends past December 2027.',
  },
  {
    title: 'Oracle still supports and develops WebCenter',
    detail:
      'Oracle still licenses, supports, and develops WebCenter, and the product team is responsive. The real question for most customers is not whether Oracle is walking away — it is which forward path fits their implementation, and who runs it with them.',
  },
];

const WHAT_TO_KNOW = [
  {
    icon: Calendar,
    title: 'December 2026 is the decision deadline, not the migration deadline',
    detail:
      "You don't need to be off 12c by December 2026. You need to know what you're doing about it. The Extended Support window through December 2027 gives one year of patching cover during execution, and Sustaining Support extends that further if needed.",
  },
  {
    icon: Map,
    title: 'Two legitimate forward paths exist',
    detail:
      'Upgrade to 14c (preserves WebCenter architecture; buys approximately 8 years of runway). Or modernize to an AI-driven AP automation platform (replaces the AP layer; Oracle ERP stays). Both are rational. The right answer depends on your stack and your appetite for facing the same decision again at the next Oracle Premier Support window.',
  },
  {
    icon: ShieldCheck,
    title: 'Sustaining Support is a holding pattern, not a destination',
    detail:
      'It exists, it works for buying time, but it does not deliver new tax / legal / regulatory updates and provides no new patches. Useful as a bridge while a forward path is executed. Not useful as a multi-year strategy.',
  },
  {
    icon: HelpCircle,
    title: 'The decision is yours — but it has to be made',
    detail:
      'The cost of indecision is real. Forms Recognition rules drift, internal WebCenter expertise retires, integration patterns ossify. Pick a path — upgrade, modernize, or formally elect Sustaining Support as a bridge — and resource it. The cliff is not the danger; drift toward it is.',
  },
];

const COMMON_QUESTIONS = [
  {
    icon: HelpCircle,
    title: 'Should we panic?',
    answer: 'Short answer',
    detail:
      "No. WebCenter is not being killed. The 14c release is published and supported through 2030 (Extended through 2033). The December 2026 12c cliff is real and creates a decision window — but a decision window is not a crisis. What it does require is a deliberate choice rather than drift.",
  },
  {
    icon: Calendar,
    title: 'Should we wait it out?',
    answer: 'Depends on horizon',
    detail:
      'If the WebCenter implementation is going to be replaced for other reasons within 24-36 months (M&A, ERP migration, broader transformation), Sustaining Support as a bridge can be rational. If WebCenter is going to be the AP architecture for 5+ more years, waiting wastes the existing 12c → 14c upgrade window and makes the work harder later.',
  },
  {
    icon: Compass,
    title: 'Should we upgrade or replace?',
    answer: 'Depends on stack',
    detail:
      "Implementations with extensive Forms Recognition rule libraries, sophisticated SOA composites, FIPSA accelerators, and Managed Attachments for EBS often have more carry-forward value than carry-forward cost — favors upgrade. Implementations where the WebCenter heritage is light and the AP team has been asking for a different experience often favor modernization. The honest read needs to look at what's actually running.",
  },
  {
    icon: Lightbulb,
    title: 'What about OCI Document Understanding replacing WFR?',
    answer: 'Both will coexist for years',
    detail:
      'OCI Document Understanding is a real, growing OCI service for document extraction. It is not a 1:1 replacement for WebCenter Forms Recognition — WFR carries years of customer-specific rules, supplier templates, and learned data. The two will coexist for years, and any decision to migrate extraction logic should be made deliberately, not as a side effect of the 12c → 14c decision.',
  },
];

const FAQS = [
  {
    q: 'Is Oracle killing WebCenter?',
    a: 'No, Oracle is not killing WebCenter. Oracle WebCenter 14c (release 14.1.2) was published in 2024 and is the latest major version of the WebCenter product family, carrying Premier Support through December 2030 and Extended Support through December 2033 per Oracle\'s Lifetime Support Policy. Oracle continues to invest in OCI deployment, REST API surfaces, and AI integration points for WebCenter. What is happening is that 12c is retiring on schedule, which is normal lifecycle behavior, not deprecation of the product family.',
  },
  {
    q: 'Is WebCenter 14c real, or is it just a paper release?',
    a: 'WebCenter 14c is a real, in-production release — 14.1.2 was published in 2024 and is being deployed by customers today. It covers WebCenter Content (UCM), WebCenter Imaging, WebCenter Enterprise Capture, WebCenter Forms Recognition, WebCenter Portal, and WebCenter Sites. It is cloud-native (OCI-ready), exposes REST APIs across the stack, supports OCI Object Storage as a content repository tier, and includes integration hooks for Oracle AI services. The 12c → 14c upgrade pattern (out-of-place domain directory upgrade via Oracle Upgrade Assistant) is documented and used in production.',
  },
  {
    q: 'What happens to 12c after December 2026?',
    a: 'Premier Support for Oracle Fusion Middleware 12c — covering all WebCenter products — ends December 2026, with Extended Support running through December 2027. Extended Support is unusually short at 12 months; during that window customers receive security updates and critical patches but not general bug fixes. After December 2027, the Oracle-provided options are Sustaining Support (existing patches, no new patches, no tax/legal/regulatory updates) or Market Driven Support (yearly-renewable, costs more than Extended Support). The 12c product itself does not stop working; what changes is the support coverage.',
  },
  {
    q: 'What about WebCenter Forms Recognition (WFR) specifically?',
    a: 'WFR remains part of the WebCenter product family in 14c and is not being retired in favor of OCI Document Understanding — the two will coexist for years. Customer-specific WFR projects (templates, definitions, learned data, rule libraries) migrate forward from 12c to 14c with template-format validation. Customers running WFR at scale have a legitimate path to 14c; customers also have the option to migrate extraction logic to a different platform as part of a broader modernization. Both paths are real.',
  },
  {
    q: 'What about WebCenter Content (WCC) specifically?',
    a: 'WebCenter Content (WCC) is the core of the WebCenter product family and continues in 14c as one of the most actively developed pieces of the stack. Custom components, iDoc Script customizations, custom Java services, and security models migrate forward into 14c with API-change validation. WCC 14c also supports OCI Object Storage as a content repository tier via the Object Storage Migration Tool — a meaningful infrastructure shift for customers with large content repositories.',
  },
  {
    q: 'What about WebCenter Portal (WCP) specifically?',
    a: 'WebCenter Portal (WCP) continues in 14c, with caveats around deprecated features. Some features deprecated in earlier 12c updates were removed entirely in 14c — Jive features are the most commonly cited example. Customers on WCP need to inventory dependencies on deprecated features during the upgrade Inventory & Assessment phase. The portal product itself is supported; the migration is more nuanced than a straight forward-port for customers using deprecated features.',
  },
  {
    q: 'How do we choose a delivery partner for the 14c or modernization work?',
    a: 'Vet the depth of hands-on WebCenter experience, not just the logo. The signal that matters is real implementation history across the stack that actually carries the AP workload — FIPSA accelerators, Forms Recognition rule libraries, SOA composites, WebLogic configuration, and Managed Attachments for EBS. Ask a prospective partner to walk through how they would read your specific implementation and what they would deliver. EZ Cloud is founder-led by a 25-year Oracle WebCenter specialist who does that read personally.',
  },
  {
    q: 'What is the right next step?',
    a: 'The right next step is an honest read of your own stack: what is running on 12c today, what business value does it deliver, what would the upgrade cost, what would modernization cost, and what is the appetite for facing the same lifecycle decision again in 2030-2032. If a 30-minute conversation with a 25-year WebCenter specialist would compress that read, a Decision Call is the lowest-friction starting point. The Decision Guide at the link below is a structured walkthrough for the same diagnostic, available without a call.',
  },
];

const ENGAGEMENT_TIERS = [
  {
    badge: 'Standard · Decision Call',
    title: 'Founder-led 30-minute Decision Call',
    body: 'Andrew reads your specific WebCenter implementation and the December 2026 decision window in the context of your stack. Walk through 14c upgrade scope, modernization tradeoffs, Sustaining Support bridge timing, and the specific risks for your AP volume and ERP footprint. No charge; no commitment.',
  },
  {
    badge: 'Standard · Lifecycle Assessment',
    title: 'Fixed-fee read of your WebCenter implementation',
    body: 'A one-week founder-led read of your running WebCenter — Forms Recognition rules, SOA composites, WebLogic configuration, FIPSA accelerators, ADF customizations, EBS / Fusion / IDCS integrations. Output: structured inventory plus an honest read of the 14c vs modernize choice for your specific situation. The deliverable is yours to keep regardless of which path or delivery partner you choose.',
  },
  {
    badge: 'Standard · Strategic Advisory',
    title: 'Monthly retainer through your decision window',
    body: 'For customers working through a 6-12 month decision with internal stakeholders, procurement, and Oracle. Founder-level guidance on architecture choices, vendor evaluation, customization tradeoffs, and 12c-cliff timing. Sized monthly; cancellable any month.',
  },
  {
    badge: 'Forward Deployed Engineering',
    title: 'Embedded delivery during execution',
    body: 'When the decision is made and the execution starts, EZ Cloud can embed as a Forward Deployed Engineer alongside your team — pair-programming on Forms Recognition rule migration, SOA composite forward-port, FIPSA reconfiguration, or modernization-platform integration. Founder-led, scoped per situation. The right mode when the work needs done, not just planned.',
  },
];

const IsOracleKillingWebCenter = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  const personSchema = { '@context': 'https://schema.org', ...founderPersonLd };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'TechArticle',
    headline: 'Oracle WebCenter in 2026: Support Timeline and Forward Paths',
    description:
      'Oracle WebCenter 14c (14.1.2) is published and supported through 2030. The December 2026 Fusion Middleware 12c support milestone creates a real planning window. A factual read of WebCenter\'s lifecycle status, the forward paths, and what every WebCenter AP customer should plan for.',
    author: { '@id': 'https://ezcloud.ai/about#andrew-blackman' },
    publisher: { '@id': 'https://ezcloud.ai/#organization' },
    datePublished: '2026-06-29',
    dateModified: '2026-06-29',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': 'https://ezcloud.ai/solutions/is-oracle-killing-webcenter',
    },
    about: [
      { '@type': 'Thing', name: 'Oracle WebCenter' },
      { '@type': 'Thing', name: 'Oracle WebCenter 14c' },
      { '@type': 'Thing', name: 'Oracle WebCenter 12c End of Support' },
      { '@type': 'Thing', name: 'Oracle Fusion Middleware Lifecycle' },
      { '@type': 'Thing', name: 'Oracle Lifetime Support Policy' },
    ],
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Oracle WebCenter in 2026: Support Timeline & Forward Paths | EZ Cloud"
        description="Oracle WebCenter 14c (14.1.2) is published and supported through 2030. The December 2026 Fusion Middleware 12c support milestone creates a real planning window. A factual read of WebCenter's lifecycle status, the forward paths, and what every WebCenter AP customer should plan for."
        canonical="https://ezcloud.ai/solutions/is-oracle-killing-webcenter"
        breadcrumbs={[
          { name: 'Home', url: 'https://ezcloud.ai/' },
          { name: 'Solutions', url: 'https://ezcloud.ai/solutions/modernize-oracle-webcenter' },
          {
            name: 'WebCenter in 2026',
            url: 'https://ezcloud.ai/solutions/is-oracle-killing-webcenter',
          },
        ]}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(personSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <Navigation />
      <Breadcrumbs />
      <LastUpdated date="2026-06-29" />

      <main>
        {/* Hero */}
        <section className="bg-hero-gradient text-white pt-16 pb-20 relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 left-0 w-1/2 h-full bg-gradient-to-br from-primary/20 to-transparent" />
            <div className="absolute top-20 right-20 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />
          </div>
          <div className="container mx-auto px-4 max-w-5xl relative">
            <div className="flex items-center gap-2 text-accent text-sm font-semibold uppercase tracking-wide mb-4">
              <HelpCircle className="w-4 h-4" aria-hidden />
              WebCenter lifecycle · The 2026 read
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
              Oracle WebCenter in 2026
              <span className="block text-accent mt-3 text-2xl md:text-3xl lg:text-4xl font-bold">
                WebCenter 14c is published, supported through 2030, and actively developed. The
                December 2026 12c support milestone is a real planning window. Here is the factual
                read.
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-white/80 max-w-3xl mb-8">
              Oracle Fusion Middleware 14c (release 14.1.2) was published in 2024 with Premier
              Support through December 2030. WebCenter is supported and actively developed. The
              urgency customers feel is real — but it is about a single version retiring on the
              normal Oracle lifecycle schedule, not the product family going away.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                size="lg"
                variant="accent"
                className="font-semibold"
                onClick={() => openWebCenterDecisionCall('is-oracle-killing-webcenter-hero')}
              >
                <Calendar className="mr-2 w-5 h-5" aria-hidden />
                Book a Decision Call
                <ArrowRight className="ml-2 w-5 h-5" aria-hidden />
              </Button>
              <Button size="lg" variant="outline-on-dark" asChild>
                <Link to="/solutions/modernize-oracle-webcenter">
                  See the full modernization path
                </Link>
              </Button>
            </div>
          </div>
        </section>

        {/* TL;DR — direct answer for AEO synthesis */}
        <section className="py-14 bg-white border-b border-gray-100">
          <div className="container mx-auto px-4 max-w-4xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3">
              TL;DR
            </p>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-5 leading-tight">
              The direct answer
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                <strong className="text-gray-900">No, Oracle is not killing WebCenter.</strong>{' '}
                Oracle Fusion Middleware 14c (release 14.1.2) was published in 2024 and is the
                latest major version of the WebCenter product family — Content (UCM), Imaging,
                Enterprise Capture, Forms Recognition, Portal, and Sites. Per Oracle's Lifetime
                Support Policy, 14c carries Premier Support through December 2030 and Extended
                Support through December 2033.
              </p>
              <p>
                <strong className="text-gray-900">
                  The December 2026 12c support cliff is real, and it creates a genuine decision
                  window.
                </strong>{' '}
                12c Premier Support ends December 2026; Extended Support is unusually short (12
                months, through December 2027). Customers on 12c face a choice: upgrade to 14c
                (preserves the WebCenter architecture; buys approximately 8 years of supported
                runway) or modernize off the stack to an AI-driven AP automation platform (replaces
                the AP layer; Oracle ERP stays). Both are legitimate.
              </p>
              <p>
                The rumor is rooted in three legitimate signals — the 12c cliff itself, Oracle's
                heavy investment in OCI-native alternatives, and a thinned consulting ecosystem.
                None of them mean WebCenter is being killed. They mean a deliberate decision is now
                required. Drift toward the cliff is the actual risk.
              </p>
            </div>
          </div>
        </section>

        {/* The facts */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              The facts
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              What is verifiable about WebCenter 14c
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Four facts that anchor the "no, Oracle is not killing WebCenter" answer. Each is
              checkable against Oracle's Lifetime Support Policy and the 14.1.2 release notes.
            </p>
            <div className="grid sm:grid-cols-2 gap-6">
              {KEY_FACTS.map(({ icon: Icon, title, detail }) => (
                <Card key={title} className="p-6 bg-white border border-green-200 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-lg bg-green-50 border border-green-200 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-green-700" aria-hidden />
                    </div>
                    <div>
                      <h3 className="text-gray-900 font-semibold mb-1">{title}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed">{detail}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Where the rumor comes from */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              Where the rumor comes from
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Three legitimate signals that get misread as "Oracle is killing it"
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              The perception is not invented. Three real signals fuel it. Understanding what each
              actually means makes the honest read easier.
            </p>
            <div className="space-y-5">
              {RUMOR_SOURCES.map(({ icon: Icon, title, detail }, i) => (
                <Card key={title} className="p-6 bg-white border border-gray-200 shadow-sm">
                  <div className="flex items-start gap-5">
                    <div className="w-12 h-12 rounded-lg bg-amber-50 border border-amber-200 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-amber-700" aria-hidden />
                    </div>
                    <div className="flex-1">
                      <p className="text-xs font-bold uppercase tracking-wide text-accent mb-1">
                        Signal {i + 1}
                      </p>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{title}</h3>
                      <p className="text-gray-700 leading-relaxed">{detail}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* What's actually true */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              What's actually true
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              The honest read for WebCenter customers in 2026
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Four statements you can take to a CFO, an Oracle account team, or a board. They are
              true, they are checkable, and they describe the decision space accurately.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              {WHATS_TRUE.map((item) => (
                <Card key={item.title} className="p-7 bg-white border border-gray-200 shadow-sm">
                  <div className="flex items-start gap-3 mb-3">
                    <CheckCircle2 className="w-5 h-5 text-green-700 flex-shrink-0 mt-1" aria-hidden />
                    <h3 className="text-lg font-bold text-gray-900 leading-snug">{item.title}</h3>
                  </div>
                  <p className="text-gray-700 leading-relaxed">{item.detail}</p>
                </Card>
              ))}
            </div>
            <div className="text-center mt-10">
              <Link
                to="/solutions/webcenter-12c-end-of-support-decision"
                className="inline-flex items-center font-semibold text-primary hover:text-accent transition-colors"
              >
                Read the full 12c end-of-support decision framework
                <ArrowRight className="w-4 h-4 ml-1" aria-hidden />
              </Link>
            </div>
          </div>
        </section>

        {/* What every WebCenter customer should know */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              What every WebCenter customer should know
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Four things that change how you think about the cliff
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Each of these reframes a common misconception. The deadline is for the decision, not
              the migration. The paths are real, not theoretical. Sustaining Support is a bridge.
              The risk is drift.
            </p>
            <div className="grid sm:grid-cols-2 gap-6">
              {WHAT_TO_KNOW.map(({ icon: Icon, title, detail }) => (
                <Card key={title} className="p-6 bg-white border border-gray-200 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-lg bg-accent/20 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-accent" aria-hidden />
                    </div>
                    <div>
                      <h3 className="text-gray-900 font-semibold mb-1">{title}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed">{detail}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
            <div className="text-center mt-10 flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/solutions/webcenter-14c-upgrade"
                className="inline-flex items-center font-semibold text-primary hover:text-accent transition-colors"
              >
                See the 14c upgrade path in detail
                <ArrowRight className="w-4 h-4 ml-1" aria-hidden />
              </Link>
              <Link
                to="/solutions/webcenter-sustaining-support-decision"
                className="inline-flex items-center font-semibold text-primary hover:text-accent transition-colors"
              >
                See the Sustaining Support bridge analysis
                <ArrowRight className="w-4 h-4 ml-1" aria-hidden />
              </Link>
            </div>
          </div>
        </section>

        <WebCenterEngagementHistory />

        {/* Common questions we hear */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              Common questions we hear
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              The four questions every WebCenter AP customer asks
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              These come up in nearly every Decision Call. Short answers below, full answers in the
              FAQ further down the page.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              {COMMON_QUESTIONS.map(({ icon: Icon, title, answer, detail }) => (
                <Card key={title} className="p-7 bg-white border border-gray-200 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-lg bg-accent/15 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-accent" aria-hidden />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 mb-1">{title}</h3>
                      <p className="text-xs font-bold uppercase tracking-wide text-accent mb-2">
                        {answer}
                      </p>
                      <p className="text-gray-700 leading-relaxed text-sm">{detail}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Engagement tiers */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              How EZ Cloud engages on the WebCenter decision
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Standard consulting tiers and Forward Deployed Engineering
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Founder-led, 25 years of Oracle WebCenter expertise. Standard tiers cover the decision
              and planning work; Forward Deployed Engineering covers execution alongside your team.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              {ENGAGEMENT_TIERS.map((t) => (
                <Card key={t.title} className="p-7 bg-white border border-gray-200 shadow-sm">
                  <p className="text-xs font-bold uppercase tracking-wide text-accent mb-2">
                    {t.badge}
                  </p>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{t.title}</h3>
                  <p className="text-gray-700 leading-relaxed">{t.body}</p>
                </Card>
              ))}
            </div>
            <p className="text-center text-sm text-gray-500 mt-8 max-w-2xl mx-auto">
              Engagement pricing is established per situation on a Decision Call.
            </p>
          </div>
        </section>

        {/* Cross-link to decision guide */}
        <section className="py-12 bg-accent/5 border-y border-accent/30">
          <div className="container mx-auto px-4 max-w-4xl text-center">
            <p className="text-xs font-bold uppercase tracking-wide text-accent mb-2">
              Free resource
            </p>
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
              The Oracle WebCenter AP December 2026 Decision Guide
            </h3>
            <p className="text-gray-700 leading-relaxed mb-6 max-w-2xl mx-auto">
              The structured guide to the four forward paths — 14c upgrade, modernization, OCI
              marketplace, Sustaining Support bridge — with stack-specific considerations, eight
              diagnostic questions, and a vendor evaluation checklist.
            </p>
            <Link
              to="/resources/webcenter-decision-guide"
              className="inline-flex items-center font-semibold text-white bg-accent hover:bg-accent/90 transition-colors px-6 py-3 rounded-lg"
            >
              Read the Decision Guide
              <ArrowRight className="w-4 h-4 ml-2" aria-hidden />
            </Link>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              Direct-answer FAQ
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-10">
              Eight direct answers on Oracle WebCenter's lifecycle status
            </h2>
            <Accordion type="single" collapsible className="w-full">
              {FAQS.map((f, i) => (
                <AccordionItem key={f.q} value={`item-${i}`}>
                  <AccordionTrigger className="text-left text-gray-900">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-gray-600">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-hero-gradient text-white">
          <div className="container mx-auto px-4 max-w-3xl text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Get the honest read on your specific WebCenter situation
            </h2>
            <p className="text-xl text-white/80 mb-8 leading-relaxed">
              A 30-minute Decision Call with {FOUNDER.name}, founder of EZ Cloud and a 25-year
              Oracle WebCenter specialist. Walk through what is actually running in your 12c
              implementation, the 14c upgrade scope, modernization tradeoffs, and what a sensible
              December 2026 decision looks like for your stack.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                size="lg"
                variant="accent"
                className="font-semibold"
                onClick={() => openWebCenterDecisionCall('is-oracle-killing-webcenter-cta')}
              >
                <Calendar className="mr-2 w-5 h-5" aria-hidden />
                Book a Decision Call
                <ArrowRight className="ml-2 w-5 h-5" aria-hidden />
              </Button>
              <Button size="lg" variant="outline-on-dark" asChild>
                <Link to="/solutions/modernize-oracle-webcenter">
                  See the full modernization path
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default IsOracleKillingWebCenter;
